import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Col, Form, message, Row } from 'antd';
import {
  DonationOutput,
  useCreateDonationWithAdditionalInfoMutation,
  useGetDonorQuery,
  useGetPreviousDonationQuery,
  DonorStatus,
  DonorOutput,
  JugularVein,
  useGetLabworkListQuery,
  LabworkOutput,
} from 'gql/types';
import { Alert, Button, Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import AdditionalDonationInfoSection from 'modules/donations/components/CreateDonationForm/AdditionalInfoSection';
import VitalsSection from 'modules/donations/components/CreateDonationForm/VitalsSection';
import { getCachedDonors } from 'modules/donor/utils/donor-cache';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { deepMerge } from 'utils/deepMerge';
import { removeDeepUndefinedFields } from 'utils/removeDeepUndefinedFields';

import { getDefaultFieldsData } from './getDefaultFieldsData';
import { getIneligibleFieldsData } from './getIneligibleFieldsData';
import { prepareDonationToCreate } from './prepareDonationToCreate';
import { createDonationSchema, CreateDonationSchemaType } from './schema';
import styles from './styles.module.scss';

import { useDebounce } from '../../../common/hooks';
import { CreateDonationAlerts } from '../CreateDonationAlerts';

const CreateDonationForm: FC = () => {
  const { t } = useTranslation('donation.CreateDonationForm');

  const [displayingAlertsAllowed, setDisplayingAlertsAllowed] = useState(false);
  const [searchParams] = useSearchParams();
  const shortDonorId = searchParams.get('donorId') || '';
  const isOffline = !navigator.onLine;

  const cachedDonor =
    isOffline && shortDonorId ? getCachedDonors()[shortDonorId] : undefined;

  const createDonationForm = useForm<CreateDonationSchemaType>({
    resolver: yupResolver(createDonationSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: getDefaultFieldsData(cachedDonor as DonorOutput),
  });

  const { debounce } = useDebounce(1200);

  const debouncedTrigger = useMemo(
    () =>
      debounce((name: string) => {
        createDonationForm.trigger(name as Path<CreateDonationSchemaType>);
      }),
    [debounce, createDonationForm]
  );

  useEffect(() => {
    const subscription = createDonationForm.watch((_, { name }) => {
      if (name) {
        debouncedTrigger(name);
      }
    });

    return () => subscription.unsubscribe();
  }, [createDonationForm.watch, debouncedTrigger]);

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
  } = createDonationForm;

  const {
    data: donorData,
    loading: loadingDonor,
    refetch: refetchDonor,
  } = useGetDonorQuery({
    skip: isOffline || !shortDonorId,
    variables: {
      input: {
        id: shortDonorId,
      },
    },
    onError: (error) => message.error(error.message),
    onCompleted: (data) => {
      const defaultFormData = getDefaultFieldsData(
        data.getDonor as DonorOutput
      );
      reset(defaultFormData);
    },
  });

  const donorId = donorData?.getDonor.id ?? cachedDonor?.id;

  const { data: donorLabworkData, loading: donorLabworkDataLoading } =
    useGetLabworkListQuery({
      skip: isOffline || !donorId,
      variables: {
        input: {
          filter: {
            donorId,
          },
        },
      },
    });

  const lastLabworkOnline = donorLabworkData?.getLabworkList.data?.reduce(
    (latest, labwork) => {
      return new Date(labwork.collectionDate) > new Date(latest.collectionDate)
        ? labwork
        : latest;
    },
    donorLabworkData?.getLabworkList.data[0]
  ) as LabworkOutput;
  const lastLabworkOffline = cachedDonor?.lastLabworkDate
    ? {
        __typename: 'LabworkOutput',
        id: 'offline',
        collectionDate: cachedDonor.lastLabworkDate,
      }
    : undefined;
  const lastLabwork = lastLabworkOnline ?? lastLabworkOffline;

  const ineligible = watch('vitalsSection.examination.ineligible');
  const ineligibleReason = watch('vitalsSection.examination.ineligibleReason');

  const isDonorArchived = donorData?.getDonor.status === DonorStatus.Archived;
  const isFormValid = !!ineligible && !!ineligibleReason ? true : isValid;
  const isSaveButtonDisabled = isDonorArchived || !isFormValid || !donorId;

  const { data: previousDonationData, loading: loadingPreviousDonation } =
    useGetPreviousDonationQuery({
      variables: {
        input: {
          donorId: donorId as string,
        },
      },
      onCompleted: (data) => {
        const previousVein = data.getPreviousDonation?.jugularVein;
        const oppositeVein =
          previousVein === JugularVein.Left
            ? JugularVein.Right
            : JugularVein.Left;
        setValue('vitalsSection.donationInfo.jugularVein', oppositeVein);
      },
      skip: !donorId,
    });

  const previousDonation = previousDonationData?.getPreviousDonation as
    | DonationOutput
    | undefined;

  const [createDonation, { loading: loadingCreatingDonation }] =
    useCreateDonationWithAdditionalInfoMutation({
      refetchQueries: ['getPreviousDonation', 'getSpecificDonorDonationsList'],
      onCompleted: () => {
        message.success(t('donationCreated'));
        reset();
        refetchDonor();
      },
      onError: (e) => {
        message.error(e.message);
      },
    });

  const submitForm = async (formData: CreateDonationSchemaType) => {
    const variables = prepareDonationToCreate({
      donorId: donorId as string,
      formData,
    });
    createDonation({
      variables,
    });
  };

  const saveHandler = handleSubmit(submitForm);

  const handleSave = async () => {
    const formData = getValues();
    await submitForm(formData);
  };

  useEffect(() => {
    const cleanedObject = removeDeepUndefinedFields(getValues());
    if (ineligible) {
      setDisplayingAlertsAllowed(false);
      const ineligibleFormValues = getIneligibleFieldsData(
        donorData?.getDonor as DonorOutput
      );

      reset(deepMerge(ineligibleFormValues, cleanedObject));
    } else {
      //TODO: for now removed the logic of reseting the fields to initial value, but it should be fixed
      // const defaultFormData = getDefaultFieldsData(
      //   donorData?.getDonor as DonorOutput
      // );

      // createDonationForm.reset(defaultFormData, { keepValues: true });
      setDisplayingAlertsAllowed(true);
    }
  }, [ineligible]);

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid,
  });

  const loading =
    loadingCreatingDonation ||
    loadingDonor ||
    loadingPreviousDonation ||
    donorLabworkDataLoading;

  return (
    <>
      <Spin spinning={loading}>
        {isDonorArchived && (
          <Alert
            type="error"
            message={t('donorArchived')}
            className={styles.alert}
          />
        )}

        <FormProvider {...createDonationForm}>
          <Form layout="vertical">
            <Row>
              <Col span={24} className={styles.saveButton}>
                <Button
                  type="primary"
                  disabled={isSaveButtonDisabled}
                  onClick={saveHandler}
                >
                  {t('save')}
                </Button>
              </Col>
              <Col span={24}>
                {displayingAlertsAllowed && !ineligible && (
                  <CreateDonationAlerts
                    previousDonationData={previousDonation}
                    lastLabwork={lastLabwork}
                  />
                )}
              </Col>
              <Col span={24}>
                <VitalsSection
                  previousDonation={previousDonation}
                  donor={donorData?.getDonor as DonorOutput}
                  sectionNamePrefix="vitalsSection"
                  formEnabled={false}
                />
              </Col>
              <Col span={24}>
                <AdditionalDonationInfoSection
                  fieldNamePrefix="additionalInfoSection"
                  formEnabled={true}
                />
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={loading}
      />
    </>
  );
};

export default CreateDonationForm;
